import {
  OperationalPeriod,
  usePeriods,
} from 'components/IAP/hooks/incidentHooks';
import { useIncidentStatusSummaries } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectIncidentGuid } from 'slices/commonSelectors';
import { filterItemsByOperationalPeriod } from 'utils/operationalPeriodUtils';
import IncidentStatusSummaryItem from './IncidentStatusSummaryItem';
import ICSWidget from 'components/IAP/planningView/widgets/ICSWidget';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useSearchParams } from 'react-router';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../../USCGPage';

type IncidentStatusSummaryProps = {
  currPeriod: OperationalPeriod | undefined;
};

const IncidentStatusSummary: React.FC<IncidentStatusSummaryProps> = ({
  currPeriod,
}) => {
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-incident-status-summary`;

  // TODO aeortic: Consider moving the resolution of incidentId to the hook itself
  const incidentId = useSelector(selectIncidentGuid);
  const {
    data: incidentStatusSummaries,
    isLoading,
    error,
  } = useIncidentStatusSummaries(incidentId || '');

  const { exportAsForm } = useIcsFormPdfPreview();

  const { data: periods } = usePeriods();

  const filteredStatusSummary = useMemo(() => {
    return filterItemsByOperationalPeriod(
      incidentStatusSummaries || [],
      currPeriod,
      periods,
      (summary) => summary.createdAt
    );
  }, [incidentStatusSummaries, currPeriod, periods]);

  const handleExport = () => {
    void exportAsForm(
      {
        formname: 'ICS_209_CG',
        data: {},
        mapSketch: null,
        periodNumber: currPeriod?.period_number,
      },
      { allowWorkspaceDownload: !!filteredStatusSummary }
    );
  };

  return (
    <>
      <ConditionalCollapsibleCard
        sectionKey={sectionKey}
        className="planning-widget"
        contentClassName="content mt-2"
        headerLeft={
          <ICSWidget.HeaderLeft
            iconName="bar_chart"
            iconColor="primary"
            title={getIncidentCardTitle('incidentStatusSummary')}
            subtitle="Summarizes incident information for the operational period"
          />
        }
        headerRight={
          <ICSWidget.HeaderRight
            icsRoles={['PSC', 'Situation Unit Leader']}
            handleExport={handleExport}
            exportText="Preview ICS 209"
          />
        }
      >
        {isLoading && (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        {error && (
          <div className="alert alert-danger">
            Error loading incident status summaries: {error.message}
          </div>
        )}
        {!isLoading && !error ? (
          <div className="safety-plans-list">
            <IncidentStatusSummaryItem statusSummary={filteredStatusSummary} />
          </div>
        ) : null}
      </ConditionalCollapsibleCard>
    </>
  );
};

export default IncidentStatusSummary;
